/* istanbul ignore file */
/**
 * Created by rathompson on 11/3/21
 * File: CustomRolesTable
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useMemo } from 'react'
import {
  Body,
  Button,
  useModal,
} from '@panwds/react-ui'
import {
  ConnectedTable,
  ConnectedTableLayout,
  TableProvider,
} from '@panwds/react-table'
import NameRenderer from './cellRenderer/NameRenderer'
import TableHeader from '../../../../common/components/table/TableHeader'
import ActionsRenderer from './cellRenderer/ActionsRenderer'
import {
  LABEL_HEADER_ROLE_DESC,
  LABEL_HEADER_CUSTOM_ROLE_NAME,
  LABEL_HEADER_ROLE_ACTIONS,
  LABEL_CUSTOM_ROLES_SUBTITLE,
  LABEL_CUSTOM_ROLES_TITLE,
  BUTTON_ADD_CUSTOM_ROLE,
  LABEL_ROLE_ACTION_EDIT,
  LABEL_ROLE_ACTION_CLONE,
  LABEL_ROLE_ACTION_DELETE,
  LABEL_INHERITED, LABEL_HEADER_ROLE_ID,
} from '../../../constants/LabelConstants'
import styled from 'styled-components'
import GlobalContext from '../../../../common/context/GlobalContext'
import InheritanceRenderer from './cellRenderer/InheritanceRenderer'
import { getVarValue } from '../../../../common/utils/Sparky'
import { FF_V2_ROLE_MODAL } from '../../../../common/utils/FeatureFlags'
import ViewRoleModal from '../modal/ViewRoleModal'
import InheritedRoleModal from '../modal/InheritedRoleModal'
import V5TableWrapper from '../../../../common/components/table/V5TableWrapper'
import { useLoadingContext } from '../../../../common/context/LoadingContext'
import type { IAM_Role } from '../../../../common/reducers/types/IAMReducer.types'
import type { VFC } from 'react'
import useReadOnly from '../../../../common/hooks/useReadOnly'
import { ROLES_PERMISSION_SET } from '../../../constants/IAMConstants'

const CONTAINER = styled(V5TableWrapper)`
  flex: 1 1;
  height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  .tw-border-solid.tw-border-gray-200.tw-border-0.tw-flex.tw-relative.tw-bg-white.tw-border.tw-overflow-auto {
    flex: 1 1;
  }
  .tw-font-lato.tw-box-border.tw-px-4.tw-py-1.tw-items-center.tw-flex.tw-h-full.tw-w-full.tw-leading-tight {
    align-items: baseline;
  }
  .tw-flex.tw-bg-white {
    flex: 1 1;
  }
`

const useV2RoleModal = getVarValue(FF_V2_ROLE_MODAL)

/**
 *   Custom roles table list
 */
const CustomRolesTable: VFC<{
  onAddClick: any,
  onEditClick: any,
  onCloneClick: any,
  onDeleteClick: any,
  canListRoles: any
}> = ({ onAddClick, onEditClick, onCloneClick, onDeleteClick, canListRoles }) => {

  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode = {} } = treeReducer

  const { iamReducer } = useContext(GlobalContext)
  const { customRoles, permissionsMap } = iamReducer
  const { loading } = useLoadingContext()
  const readOnly = useReadOnly(selectedTreeNode?.id, ROLES_PERMISSION_SET)

  const viewRoleModal = useModal((useV2RoleModal ? ViewRoleModal : InheritedRoleModal) as any)

  const onNameClick = useCallback(({ row }) => {
    viewRoleModal.openModal({ role: row?.original, permissionsMap })
  }, [viewRoleModal, permissionsMap])

  const onActionClick = useCallback((action, role) => {
    switch (action) {
      case LABEL_ROLE_ACTION_EDIT:
        onEditClick(role)
        break
      case LABEL_ROLE_ACTION_CLONE:
        onCloneClick(role)
        break
      case LABEL_ROLE_ACTION_DELETE:
        onDeleteClick(role)
        break
      default:
        // do nothing
    }
  }, [onCloneClick, onDeleteClick, onEditClick])

  const columns = useMemo(() => [
    {
      accessor: (role: IAM_Role) => role.label || role.name,
      Header: LABEL_HEADER_CUSTOM_ROLE_NAME,
      Cell: (props: any) => <NameRenderer { ...props } onClick={onNameClick.bind(this, props)} />,
      width: 232,
      id: 'name',
    },
    {
      accessor: (role: IAM_Role) => role.id,
      Header: LABEL_HEADER_ROLE_ID,
      Cell: (col: { value: string }) => (
        <div className='tw-whitespace-normal tw-w-full'>
          <Body size="lg" truncate>{col.value}</Body>
        </div>
      ),
      width: 232,
      id: 'id',
    },
    {
      accessor: (role: IAM_Role) => role?.tsg_id,
      Header: LABEL_INHERITED,
      width: 232,
      Cell: InheritanceRenderer,
      id: 'inherited_from',
    },
    {
      accessor: (role: IAM_Role) => role?.description,
      Header: LABEL_HEADER_ROLE_DESC,
      Cell: (col: { value: string }) => (
        <div className='tw-whitespace-normal tw-w-full'>
          <Body size="lg" truncate>{col.value}</Body>
        </div>
      ),
      width: 300,
      id: 'description',
    },
    {
      Header: LABEL_HEADER_ROLE_ACTIONS,
      Cell: (props: any) => <ActionsRenderer {...props} onActionClick={onActionClick} />,
      id: 'actions',
      sticky: true,
      stickyPosition: 'right' as 'right'
    },
  ], [onNameClick, onActionClick])

  return (
    <CONTAINER>
      <TableProvider
        isLoading={loading}
        columns={columns}
        data={customRoles}
        enableResizeColumns
      >
        <TableHeader title={LABEL_CUSTOM_ROLES_TITLE} subtitle={LABEL_CUSTOM_ROLES_SUBTITLE}>
          <Button appearance='primary' onClick={onAddClick} isLoading={loading} disabled={loading || readOnly}>{BUTTON_ADD_CUSTOM_ROLE}</Button>
        </TableHeader>
        <ConnectedTableLayout
          sidecar={<div/>}
          overflow
          resultsSummary={false}
        >
          <ConnectedTable
            emptyStateProps={{
              heading: canListRoles ? 'No Results Available' : 'You don\'t have access to view this page.',
            }}
            showFooter={false}
          />
        </ConnectedTableLayout>
      </TableProvider>
    </CONTAINER>
  )
}

CustomRolesTable.propTypes = {}
export default CustomRolesTable
